import {
  defineMessages,
  type FormatDateOptions,
  type IntlShape,
  type MessageDescriptor,
} from "react-intl";

const messages = defineMessages({
  // Compact scale (default): no suffix, falls back to an absolute date after a week.
  just_now: { id: "relative_time.just_now", defaultMessage: "now" },
  seconds: { id: "relative_time.seconds", defaultMessage: "{number}s" },
  minutes: { id: "relative_time.minutes", defaultMessage: "{number}m" },
  hours: { id: "relative_time.hours", defaultMessage: "{number}h" },
  days: { id: "relative_time.days", defaultMessage: "{number}d" },

  // Suffixed scale: reproduces the copy that shipped in sessions.tsx / news-card.tsx.
  // Previously hardcoded English; now translatable like everything else.
  just_now_ago: {
    id: "relative_time.ago.just_now",
    defaultMessage: "just now",
  },
  minutes_ago: {
    id: "relative_time.ago.minutes",
    defaultMessage: "{number}m ago",
  },
  hours_ago: { id: "relative_time.ago.hours", defaultMessage: "{number}h ago" },
  days_ago: { id: "relative_time.ago.days", defaultMessage: "{number}d ago" },
  months_ago: {
    id: "relative_time.ago.months",
    defaultMessage: "{number, plural, one {# month} other {# months}} ago",
  },
  years_ago: {
    id: "relative_time.ago.years",
    defaultMessage: "{number, plural, one {# year} other {# years}} ago",
  },

  // Countdown.
  expired: { id: "time_remaining.expired", defaultMessage: "Expired" },
  moments_remaining: {
    id: "time_remaining.moments",
    defaultMessage: "Moments remaining",
  },
  seconds_remaining: {
    id: "time_remaining.seconds",
    defaultMessage: "{number, plural, one {# second} other {# seconds}} left",
  },
  minutes_remaining: {
    id: "time_remaining.minutes",
    defaultMessage: "{number, plural, one {# minute} other {# minutes}} left",
  },
  hours_remaining: {
    id: "time_remaining.hours",
    defaultMessage: "{number, plural, one {# hour} other {# hours}} left",
  },
  days_remaining: {
    id: "time_remaining.days",
    defaultMessage: "{number, plural, one {# day} other {# days}} left",
  },
});

export const dateFormatOptions: FormatDateOptions = {
  hour12: true,
  year: "numeric",
  month: "short",
  day: "2-digit",
  hour: "numeric",
  minute: "2-digit",
};

const shortDateFormatOptions: FormatDateOptions = {
  month: "short",
  day: "numeric",
};

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const MONTH = 30 * DAY;
const YEAR = 365 * DAY;

const MAX_DELAY = 2147483647;
const UPDATE_FLOOR = 10 * SECOND;

/** A countdown never displays a unit coarser than days, so it never needs to tick slower. */
const COUNTDOWN_MAX_UNIT = DAY;

export type TimeAgoVariant = "compact" | "suffixed";

type VariantConfig =
  | {
      /** Switches to an absolute date once the timestamp is older than `absoluteAfter`. */
      scale: "absolute-fallback";
      justNowBelow: number;
      absoluteAfter: number;
      copy: {
        justNow: MessageDescriptor;
        seconds: MessageDescriptor;
        minutes: MessageDescriptor;
        hours: MessageDescriptor;
        days: MessageDescriptor;
      };
    }
  | {
      /** Stays relative forever, escalating through months and years. */
      scale: "unbounded";
      justNowBelow: number;
      copy: {
        justNow: MessageDescriptor;
        minutes: MessageDescriptor;
        hours: MessageDescriptor;
        days: MessageDescriptor;
        months: MessageDescriptor;
        years: MessageDescriptor;
      };
    };

const VARIANTS: Record<TimeAgoVariant, VariantConfig> = {
  compact: {
    scale: "absolute-fallback",
    justNowBelow: 10 * SECOND,
    absoluteAfter: 7 * DAY,
    copy: {
      justNow: messages.just_now,
      seconds: messages.seconds,
      minutes: messages.minutes,
      hours: messages.hours,
      days: messages.days,
    },
  },
  suffixed: {
    scale: "unbounded",
    justNowBelow: MINUTE,
    copy: {
      justNow: messages.just_now_ago,
      minutes: messages.minutes_ago,
      hours: messages.hours_ago,
      days: messages.days_ago,
      months: messages.months_ago,
      years: messages.years_ago,
    },
  },
};

const selectUnit = (absDelta: number): number => {
  if (absDelta < MINUTE) return SECOND;
  if (absDelta < HOUR) return MINUTE;
  if (absDelta < DAY) return HOUR;
  if (absDelta < MONTH) return DAY;
  if (absDelta < YEAR) return MONTH;
  return YEAR;
};

export const getNextUpdateDelay = (
  timestamp: string,
  now: number,
  variant: TimeAgoVariant = "compact",
): number => {
  const config = VARIANTS[variant];
  const delta = new Date(timestamp).getTime() - now;
  const absDelta = Math.abs(delta);
  const isPast = delta < 0;

  // Past the fallback cutoff we render a fixed date, so there is nothing left to tick.
  if (
    isPast &&
    config.scale === "absolute-fallback" &&
    absDelta >= config.absoluteAfter
  ) {
    return MAX_DELAY;
  }

  // While the "just now" copy is showing, the next visible change is at that threshold,
  // not at the next second boundary.
  if (isPast && absDelta < config.justNowBelow) {
    return Math.max(UPDATE_FLOOR, config.justNowBelow - absDelta);
  }

  const unit = isPast
    ? selectUnit(absDelta)
    : Math.min(selectUnit(absDelta), COUNTDOWN_MAX_UNIT);
  const remainder = Math.abs(delta % unit);
  const delay = isPast
    ? Math.max(UPDATE_FLOOR, unit - remainder)
    : Math.max(UPDATE_FLOOR, remainder);

  // `unit` can be MONTH/YEAR for the unbounded variant, which overflows setTimeout's
  // 32-bit signed delay — clamp so we just recheck sooner instead of firing instantly.
  return Math.min(delay, MAX_DELAY);
};

export const timeAgoString = (
  intl: IntlShape,
  date: Date,
  now: number,
  variant: TimeAgoVariant = "compact",
): string => {
  const config = VARIANTS[variant];
  const delta = now - date.getTime();

  const format = (message: MessageDescriptor, number: number) =>
    intl.formatMessage(message, { number });

  if (delta < config.justNowBelow) {
    return intl.formatMessage(config.copy.justNow);
  }

  if (delta < MINUTE && config.scale === "absolute-fallback") {
    return format(config.copy.seconds, Math.floor(delta / SECOND));
  }

  if (delta < HOUR) {
    return format(config.copy.minutes, Math.floor(delta / MINUTE));
  }

  if (delta < DAY) {
    return format(config.copy.hours, Math.floor(delta / HOUR));
  }

  if (config.scale === "unbounded") {
    if (delta < MONTH) return format(config.copy.days, Math.floor(delta / DAY));
    if (delta < YEAR)
      return format(config.copy.months, Math.floor(delta / MONTH));
    return format(config.copy.years, Math.floor(delta / YEAR));
  }

  if (delta < config.absoluteAfter) {
    return format(config.copy.days, Math.floor(delta / DAY));
  }

  const sameYear = date.getFullYear() === new Date(now).getFullYear();

  return intl.formatDate(
    date,
    sameYear
      ? shortDateFormatOptions
      : { ...shortDateFormatOptions, year: "numeric" },
  );
};

export const timeRemainingString = (
  intl: IntlShape,
  date: Date,
  now: number,
): string => {
  const delta = date.getTime() - now;

  if (delta <= 0) return intl.formatMessage(messages.expired);
  if (delta < 10 * SECOND)
    return intl.formatMessage(messages.moments_remaining);

  if (delta < MINUTE) {
    return intl.formatMessage(messages.seconds_remaining, {
      number: Math.floor(delta / SECOND),
    });
  }
  if (delta < HOUR) {
    return intl.formatMessage(messages.minutes_remaining, {
      number: Math.floor(delta / MINUTE),
    });
  }
  if (delta < DAY) {
    return intl.formatMessage(messages.hours_remaining, {
      number: Math.floor(delta / HOUR),
    });
  }

  return intl.formatMessage(messages.days_remaining, {
    number: Math.floor(delta / DAY),
  });
};
